// Filter panel (DESIGN §2 screen 5 — Q3 + Q10).
//
// Three controls, all of which the backend accepts as query params so the
// server stays the single source of truth for filtering (v2 had a filter that
// "was once a completely ineffective no-op", V2 audit §8.6 — a client-only
// filter is exactly how that happens).
//
// Q10 — the daily cap is user-adjustable. DESIGN §5 calls it the primary
// density exit, and the bench readout below the selector is what makes the
// consequence visible before the user commits: at cap 3 a 5-item day shows
// "+2", at cap 1 it shows "+4".

import { BottomSheet } from "@/components/patterns/BottomSheet";
import { Button } from "@/components/ui/button";
import {
  CATEGORY_ICON,
  CATEGORY_LABEL,
  FILTER_CATEGORY_ORDER,
} from "@/components/calendar/calendarIcons";
import { useSheetVariant } from "@/lib/hooks/useSheetVariant";
import type { CalendarCategory, OwnerFilter } from "@/lib/api/calendar";
import { cn } from "@/lib/utils/cn";

const CAP_CHOICES = [1, 2, 3, 5, 10] as const;

export interface FilterState {
  categories: CalendarCategory[];
  owner: OwnerFilter;
  maxPerDay: number;
}

export interface FilterPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  value: FilterState;
  onChange: (next: FilterState) => void;
  /** Item count of the busiest day in view, for the cap bench. */
  busiestDayItems: number;
}

export function FilterPanel({
  open,
  onOpenChange,
  value,
  onChange,
  busiestDayItems,
}: FilterPanelProps) {
  const { variant, desktopWidth, isMobile } = useSheetVariant({ desktopWidth: 440 });

  function toggleCategory(c: CalendarCategory) {
    const has = value.categories.includes(c);
    onChange({
      ...value,
      categories: has
        ? value.categories.filter((x) => x !== c)
        : [...value.categories, c],
    });
  }

  return (
    <BottomSheet
      open={open}
      onOpenChange={onOpenChange}
      variant={variant}
      className={cn("overflow-y-auto", !isMobile && `w-[${desktopWidth}px]`)}
      data-testid="calendar-filter-panel-body"
    >
      <div className="flex flex-col gap-s-5" data-testid="calendar-filter-panel">
        <header>
          <h2 className="text-h2 text-ink-1">篩選</h2>
          <p className="mt-s-1 text-body-sm text-ink-2">顯示哪些</p>
        </header>

        <fieldset className="flex flex-col gap-s-2">
          <legend className="text-micro uppercase tracking-wide text-ink-3">顯示類型</legend>
          <div className="flex flex-wrap gap-s-2">
            {FILTER_CATEGORY_ORDER.map((c) => {
              const on = value.categories.includes(c);
              const Icon = CATEGORY_ICON[c];
              return (
                <button
                  key={c}
                  type="button"
                  aria-pressed={on}
                  onClick={() => toggleCategory(c)}
                  className={cn(
                    "tap-target inline-flex items-center gap-s-2 rounded-pill border px-s-3 text-body-sm",
                    on
                      ? "border-ink-1 bg-ink-1 text-paper"
                      : "border-hairline-strong text-ink-2 hover:bg-black/5",
                  )}
                  data-testid={`calendar-filter-category-${c}`}
                >
                  <Icon size={12} strokeWidth={2} aria-hidden="true" />
                  {CATEGORY_LABEL[c]}
                </button>
              );
            })}
          </div>
        </fieldset>

        <fieldset className="flex flex-col gap-s-2">
          <legend className="text-micro uppercase tracking-wide text-ink-3">顯示誰的</legend>
          <div className="grid grid-cols-2 gap-s-2">
            {(
              [
                { key: "mine", label: "你的事" },
                { key: "partner", label: "TA 的事" },
              ] as const
            ).map((opt) => {
              // "all" is represented by BOTH toggles on — turning one off is
              // what narrows the view, which is the mental model the mockup
              // uses ("關掉「TA 的事」時，你的月曆會安靜很多").
              const on = value.owner === "all" || value.owner === opt.key;
              // The query vocabulary has no "neither", so from `all` a click
              // must mean "hide this one" rather than toggling to a state the
              // filter cannot express.
              const nextOwner: OwnerFilter =
                value.owner === "all"
                  ? opt.key === "mine"
                    ? "partner"
                    : "mine"
                  : on
                    ? value.owner
                    : "all";
              return (
                <button
                  key={opt.key}
                  type="button"
                  aria-pressed={on}
                  onClick={() => onChange({ ...value, owner: nextOwner })}
                  className={cn(
                    "tap-target justify-center rounded-pill border px-s-3 text-body-sm",
                    on
                      ? "border-ink-1 bg-ink-1 text-paper"
                      : "border-hairline-strong text-ink-2 hover:bg-black/5",
                  )}
                  data-testid={`calendar-filter-owner-${opt.key}`}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
          <p className="text-meta text-ink-2">
            關掉後只看自己的事。
          </p>
        </fieldset>

        <fieldset className="flex flex-col gap-s-2">
          <legend className="text-micro uppercase tracking-wide text-ink-3">每日顯示上限</legend>
          <div className="grid grid-cols-5 gap-s-2">
            {CAP_CHOICES.map((n) => (
              <button
                key={n}
                type="button"
                aria-pressed={value.maxPerDay === n}
                onClick={() => onChange({ ...value, maxPerDay: n })}
                className={cn(
                  "tap-target justify-center rounded-pill border text-body-sm",
                  value.maxPerDay === n
                    ? "border-ink-1 bg-ink-1 text-paper"
                    : "border-hairline-strong text-ink-2 hover:bg-black/5",
                )}
                data-testid={`calendar-filter-cap-${n}`}
              >
                {n}
              </button>
            ))}
          </div>
          <div className="flex items-center justify-between text-meta text-ink-2">
            <span>
              超出後顯示 <strong className="font-semibold text-ink-1">+N</strong>
              ，點擊進入日詳情
            </span>
            <span className="font-semibold tabular-nums text-you-strong">
              N = {value.maxPerDay}
            </span>
          </div>
          <CapBench
            busiestDayItems={busiestDayItems}
            cap={value.maxPerDay}
          />
        </fieldset>

        <Button type="button" onClick={() => onOpenChange(false)} data-testid="calendar-filter-done">
          完成
        </Button>
      </div>
    </BottomSheet>
  );
}

function CapBench({ busiestDayItems, cap }: { busiestDayItems: number; cap: number }) {
  const shown = Math.min(cap, busiestDayItems);
  const overflow = Math.max(0, busiestDayItems - cap);
  const pct = busiestDayItems > 0 ? Math.round((shown / busiestDayItems) * 100) : 0;
  return (
    <div
      className="flex items-center gap-s-2"
      data-testid="calendar-filter-bench"
      aria-label={`每日上限 ${cap} 時，最忙的一天會顯示 ${shown} 項`}
    >
      <span className="shrink-0 text-meta text-ink-3">最忙一天</span>
      <span className="h-2 flex-1 overflow-hidden rounded-pill bg-paper-calm">
        <span
          className="block h-full rounded-pill bg-ink-2/70"
          style={{ width: `${pct}%` }}
        />
      </span>
      <span className="shrink-0 text-meta tabular-nums text-ink-2">
        {shown} 項{overflow > 0 ? ` +${overflow}` : ""}
      </span>
    </div>
  );
}
